import React, {
  FunctionComponent,
  ReactElement,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import styled from 'styled-components';
import { v1 as uuidv1 } from 'uuid';
import { useIntl, IntlShape, defineMessages, FormattedMessage } from 'react-intl';
import BasicInput from '@empiriecom/module-components/FormControls/Input/BasicInput';
import Notification from '@empiriecom/module-components/Notification';
import Modal from '@empiriecom/module-components/Modal';
import Label from '@empiriecom/module-components/FormControls/Label';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import useDebounce from '@empiriecom/module-components/hooks/useDebounce';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { AttributeBlock, AttributeValueObject } from '@/src/interfaces';
import { useMadeToMeasure } from '../ProductContext';

const messages = defineMessages({
  engravingMandatory: {
    defaultMessage: 'Bitte gib deinen Gravurtext ein.',
    id: 'engraving.error.mandatory',
  },
  engravingMaxLength: {
    defaultMessage: 'Dein Gravurtext ist zu lang.',
    id: 'engraving.error.maxLength',
  },
  engravingModalLink: {
    defaultMessage: 'Weitere Informationen zum Gravurservice',
    id: 'engraving.modallink.text',
  },
  engravingPlaceholder: {
    defaultMessage: 'Gib hier deine Wunschgravur ein',
    id: 'engraving.input.placeholder',
  },
  maxLengthIndicator: {
    defaultMessage: '{length} / {maxlength} Zeichen',
    id: 'engraving.error.maxLengthIndicator',
  },
  serialnumberMandatory: {
    defaultMessage: 'Bitte gib die Seriennummer ein.',
    id: 'serialnumber.error.mandatory',
  },
  serialnumberModalLink: {
    defaultMessage: 'Hinweise zu dem Maßartikel',
    id: 'serialnumber.modallink.text',
  },
  serialnumberPlaceholder: {
    defaultMessage: 'Seriennummer eingeben',
    id: 'serialnumber.input.placeholder',
  },
});

const InputContainer = styled.div`
  margin-bottom: 0.75rem;
`;

const LabelAndLength = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const MaxLengthCell = styled.div<{ error: boolean }>`
  text-align: right;
  margin-left: auto;
  font-size: 0.75rem;
  color: ${({ theme, error }) => (error ? theme.error.color.normal : theme.greyDark.color.normal)};
`;

const EngravingInformation = styled.div`
  margin-top: 1.375rem;
  font-size: 0.813rem;
`;

const EngravingInformationList = styled.ul`
  margin: 0;
  padding: 0 0 0 0.5rem;
  li {
    font-size: 0.75rem;
    line-height: 1.25rem;
  }
`;

const ModalLink = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  &&:hover {
    text-decoration: underline;
  }
`;

interface EngravingInputProps {
  engravingBlock: AttributeBlock;
  forceMaxLength?: boolean;
  onChange?: (value: string) => void;
}

const validateInput = (
  intl: IntlShape,
  mandatory: boolean,
  maxLength: number,
  setError: Dispatch<SetStateAction<string>>,
  type: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  value: string,
) => {
  switch (type) {
    case 'serialnumber':
      if (mandatory && value?.length < 1) {
        setError(intl.formatMessage(messages.serialnumberMandatory));
        return;
      }
      break;
    case 'engraving':
      if (value?.length > maxLength) {
        setError(intl.formatMessage(messages.engravingMaxLength));
        return;
      }
      if (mandatory && value?.length < 1) {
        setError(intl.formatMessage(messages.engravingMandatory));
        return;
      }
      break;
    default:
  }
  setError(null);
};

const EngravingInput: FunctionComponent<EngravingInputProps> = ({
  engravingBlock,
  forceMaxLength = false,
  onChange,
}): ReactElement => {
  const {
    engraving,
    setEngraving,
    setEngravingError,
    engravingError,
    showEngravingError,
    setShowEngravingError,
  } = useMadeToMeasure();
  const [stateValue, setStateValue] = useState<string>(engraving || '');
  const [isInformationOpen, setIsInformationOpen] = useState<boolean>(false);

  const dispatchGTMEvent = useTracking();
  const intl = useIntl();
  const debouncedValue = useDebounce(stateValue, 500);
  const ArrowRight = getIcon('ArrowMiniRight');

  const { headline, mandatory, maxLength, type } = engravingBlock.values.map(
    (value) => value.values[0] as AttributeValueObject,
  )[0];

  useEffect(() => validateInput(intl, mandatory, maxLength, setEngravingError, type, stateValue));

  useEffect(() => setShowEngravingError(false), [engravingError]);

  useEffect(() => {
    if (engravingError === null && typeof debouncedValue !== 'undefined') {
      setEngraving(debouncedValue);
    } else if (engravingError !== null) {
      setEngravingError(engravingError);
    }
  }, [debouncedValue]);

  const inputId = uuidv1();
  let placeholder: string = '';
  let modalLinkText: string = '';
  switch (type) {
    case 'engraving':
      placeholder = intl.formatMessage(messages.engravingPlaceholder);
      modalLinkText = intl.formatMessage(messages.engravingModalLink);
      break;
    case 'serialnumber':
      placeholder = intl.formatMessage(messages.serialnumberPlaceholder);
      modalLinkText = intl.formatMessage(messages.serialnumberModalLink);
      break;
    default:
  }

  return (
    <InputContainer data-testid="engraving-input-wrapper">
      <div data-testid="engraving-wrapper">
        <LabelAndLength>
          <div>
            <Label htmlFor={inputId}>{headline}</Label>
          </div>
          {maxLength && (
            <MaxLengthCell data-testid="engraving-max-length" error={stateValue.length > maxLength}>
              {intl.formatMessage(messages.maxLengthIndicator, {
                length: stateValue.length,
                maxlength: maxLength,
              })}
            </MaxLengthCell>
          )}
        </LabelAndLength>
        <BasicInput
          error={showEngravingError || forceMaxLength ? engravingError : null}
          id={inputId}
          onChange={(event) => {
            const sanitizedValue = event.target.value
              .toLowerCase()
              .replace('ä', 'ae')
              .replace('ö', 'oe')
              .replace('ü', 'ue')
              .replace('ß', 'ss')
              .toUpperCase();
            setStateValue(sanitizedValue);
            if (typeof onChange === 'function') {
              onChange(sanitizedValue);
            }
          }}
          placeholder={placeholder}
          value={stateValue}
        />
        <EngravingInformation>
          {type === 'engraving' && (
            <Notification
              isInline
              level="info"
              content={
                <EngravingInformationList>
                  <li>
                    <FormattedMessage
                      defaultMessage="Die Eingabe erfolgt in Großbuchstaben"
                      id="engraving-information.li-1"
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      defaultMessage="Umlaute müssen als AE, UE etc. geschrieben werden"
                      id="engraving-information.li-2"
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      defaultMessage="Deine Eingabe wird automatisch auf die benötigte Formatierung umgewandelt"
                      id="engraving-information.li-3"
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      defaultMessage="Du kannst Buchstaben, Zahlen, Punkte, Striche und Leerzeichen verwenden"
                      id="engraving-information.li-4"
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      defaultMessage="Bei teilbaren Artikeln gebe bitte beide Namen mit einem Bindestrich getrennt (z.B. PETER - PETRA) in das Formularfeld ein"
                      id="engraving-information.li-5"
                    />
                  </li>
                </EngravingInformationList>
              }
            />
          )}
          <ModalLink
            data-testid="engraving-modal-link"
            onClick={() => {
              setIsInformationOpen(true);
              dispatchGTMEvent({
                event: 'ga_event',
                eventAction: 'viewLayer',
                eventCategory: 'noteLayer',
                eventLabel: 'detailview',
                eventValue: modalLinkText,
              });
            }}
          >
            {modalLinkText}
            <ArrowRight />
          </ModalLink>
          <Modal
            isOpen={isInformationOpen}
            onRequestClose={() => setIsInformationOpen(false)}
            headline={modalLinkText}
            testId="engraving-modal"
          >
            {type === 'engraving' && (
              <>
                <p>
                  <FormattedMessage
                    defaultMessage="Mit unserem gratis Gravur-Service wird der Artikel noch persönlicher."
                    id="engraving-information.p1"
                  />
                </p>
                <p>
                  <FormattedMessage
                    defaultMessage="Bitte benutze bei Bestickungen das Gravurtext Feld."
                    id="engraving-information.p2"
                  />
                </p>
                <p>
                  <FormattedMessage
                    defaultMessage="Selbstverständlich ist auch die Nachgravur von ungravierten, bei uns gekauften Trauringen kostenlos. Einfach Ring und Gravur-Wunschkarte an die auf der beiliegenden Karte angegebenen Adresse einsenden."
                    id="engraving-information.p3"
                  />
                </p>
                <p>
                  <FormattedMessage
                    defaultMessage="Bitte beachte, dass gravierte Artikel vom Umtausch ausgeschlossen sind."
                    id="engraving-information.p4"
                  />
                </p>
              </>
            )}
            {type === 'serialnumber' && (
              <>
                <p>
                  <FormattedMessage
                    defaultMessage="Damit wir dein Produkt schnellstmöglich nach deinen Anforderungen fertigen können, hier einige Hinweise:"
                    id="serialnumber-information.p1"
                  />
                </p>
                <p>
                  <FormattedMessage
                    defaultMessage="Bei Produkten für dein Dachfenster teile uns bitte die jeweilige Typen-/Seriennummer deines Dachfensters mit. Du findest sie üblicherweise im Rahmen deines Dachfensters"
                    id="serialnumber-information.p2"
                  />
                </p>
                <p>
                  <FormattedMessage
                    defaultMessage='Bei Produkten, bei welchen die Bedienungsseite definiert werden muss, sollte die Bedienungsseite "LINKS" oder "RECHTS" angegeben werden.'
                    id="serialnumber-information.p3"
                  />
                </p>
              </>
            )}
          </Modal>
        </EngravingInformation>
      </div>
    </InputContainer>
  );
};

export default EngravingInput;
